import { AppError, isAppError } from '@pointfive/contracts';
import { useCallback, useEffect, useState } from 'react';

/** A per-screen read.
 *
 *  Phase 1 gave the whole UI one object: `DemoData`, composed from 28 procedure calls on every page load. That was the
 *  right shape for a mock layer and the wrong shape for a real one — it costs ~43 rate-limit tokens per load whether or
 *  not the screen needs any of it (DEC-024). This hook is the alternative: a screen asks for what it renders, when it
 *  is opened.
 *
 *  It returns the four states a real read has, not two. A screen that renders `data ?? []` while a request is in flight
 *  is asserting "there is nothing here" before it knows, and a screen that swallows an error shows stale or empty
 *  figures as if they were current — which is what ground rule 3 forbids. `@pointfive/ui` carries a component for each
 *  of these states so the distinction is visible rather than implied. */
export type ProcedureState<T> =
  | { status: 'loading'; data: null; error: null }
  | { status: 'ready'; data: T; error: null }
  | { status: 'failed'; data: null; error: AppError };

export interface ProcedureResult<T> {
  readonly state: ProcedureState<T>;
  /** Re-runs the read. Bound to the retry affordance on the failed and degraded states. */
  readonly reload: () => void;
}

export function useProcedure<T>(read: () => Promise<T>, deps: readonly unknown[] = []): ProcedureResult<T> {
  const [state, setState] = useState<ProcedureState<T>>({ status: 'loading', data: null, error: null });
  const [nonce, setNonce] = useState(0);

  // `read` is recreated on every render by callers that inline it, so the effect keys off the caller's own deps plus
  // the reload nonce rather than off the function identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(read, deps);

  useEffect(() => {
    let live = true;
    setState({ status: 'loading', data: null, error: null });
    run().then(
      (data) => {
        if (live) setState({ status: 'ready', data, error: null });
      },
      (cause: unknown) => {
        if (!live) return;
        // An unexpected rejection still has to reach the screen as something it can render; wrapping it keeps the
        // failed state's contract (`code`, `message`) intact rather than leaking a raw Error.
        const error = isAppError(cause)
          ? cause
          : new AppError({
              code: 'INTERNAL',
              message: cause instanceof Error ? cause.message : 'Something went wrong.',
              requestId: 'client',
            });
        setState({ status: 'failed', data: null, error });
      },
    );
    return () => {
      live = false;
    };
  }, [run, nonce]);

  return { state, reload: useCallback(() => setNonce((n) => n + 1), []) };
}
